import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'

export type AuditSourceId = 'v3' | 'backoffice'

export interface AuditSourceSnapshot {
  entries: AuditLogs[]
  canLoadMore: boolean
  failed: boolean
}

export interface MergedAuditSnapshot {
  entries: AuditLogs[]
  waitingFor: AuditSourceId[]
  hasMore: boolean
}

const SOURCE_ORDER: AuditSourceId[] = ['v3', 'backoffice']

export function compareAuditEntries(a: AuditLogs, b: AuditLogs): number {
  if (a.timestamp !== b.timestamp) {
    return b.timestamp - a.timestamp
  }
  if (a.id > b.id) {
    return -1
  }
  if (a.id < b.id) {
    return 1
  }
  return 0
}

export function lastPageAddsNewEntry(pages: AuditLogs[][]): boolean {
  const lastPage = pages.at(-1)
  if (lastPage === undefined) {
    return false
  }
  const earlierIds = new Set(
    pages.slice(0, -1).flatMap((page) => page.map((entry) => entry.id)),
  )
  return lastPage.some((entry) => !earlierIds.has(entry.id))
}

function pickNextSource(
  sources: Record<AuditSourceId, AuditSourceSnapshot>,
  positions: Record<AuditSourceId, number>,
): AuditSourceId | undefined {
  let picked: AuditSourceId | undefined
  for (const id of SOURCE_ORDER) {
    const head = sources[id].entries[positions[id]]
    if (head === undefined) {
      continue
    }
    if (
      picked === undefined ||
      compareAuditEntries(head, sources[picked].entries[positions[picked]]) < 0
    ) {
      picked = id
    }
  }
  return picked
}

function hasUnseenOrLoadable(
  sources: Record<AuditSourceId, AuditSourceSnapshot>,
  positions: Record<AuditSourceId, number>,
  seen: Set<string>,
): boolean {
  return SOURCE_ORDER.some(
    (id) =>
      sources[id].canLoadMore ||
      sources[id].entries
        .slice(positions[id])
        .some((entry) => !seen.has(entry.id)),
  )
}

export function mergeAuditSources(
  sources: Record<AuditSourceId, AuditSourceSnapshot>,
  limit: number,
): MergedAuditSnapshot {
  const positions: Record<AuditSourceId, number> = { v3: 0, backoffice: 0 }
  const seen = new Set<string>()
  const entries: AuditLogs[] = []

  while (entries.length < limit) {
    const waitingFor = SOURCE_ORDER.filter(
      (id) =>
        positions[id] >= sources[id].entries.length && sources[id].canLoadMore,
    )
    if (waitingFor.length > 0) {
      return { entries, waitingFor, hasMore: true }
    }

    const picked = pickNextSource(sources, positions)
    if (picked === undefined) {
      return { entries, waitingFor: [], hasMore: false }
    }

    const entry = sources[picked].entries[positions[picked]]
    positions[picked] += 1
    if (seen.has(entry.id)) {
      continue
    }
    seen.add(entry.id)
    entries.push(entry)
  }

  return {
    entries,
    waitingFor: [],
    hasMore: hasUnseenOrLoadable(sources, positions, seen),
  }
}
